import { useState, useCallback, useEffect, useRef } from 'react';
import { DebugEntry, DebugLevel, DebugModule, DebugConfig, DebugStats } from '../types/debug';

// Default configuration
const DEFAULT_CONFIG: DebugConfig = {
  enabled: process.env.NODE_ENV === 'development',
  level: 'debug',
  modules: ['serp-tool', 'dataforseo-tool', 'ai-analyst', 'explore', 'snowflake', 'qlik', 'auth', 'api', 'general'],
  maxEntries: 1000,
  persistLogs: true,
  showInConsole: false
};

// Level hierarchy for filtering
const LEVEL_HIERARCHY: Record<DebugLevel, number> = {
  'trace': 0,
  'debug': 1,
  'info': 2,
  'warn': 3,
  'error': 4
};

class DebugLogger {
  private entries: DebugEntry[] = [];
  private config: DebugConfig = { ...DEFAULT_CONFIG };
  private listeners: Set<() => void> = new Set();
  private storageKey = 'totalserp-debug-logs';

  constructor() {
    this.loadConfig();
    this.loadPersistedLogs();
  }

  private loadConfig() {
    try {
      const saved = localStorage.getItem('totalserp-debug-config');
      if (saved) {
        this.config = { ...DEFAULT_CONFIG, ...JSON.parse(saved) };
      }
    } catch (error) {
      console.warn('Failed to load debug config:', error);
    }
  }

  private saveConfig() {
    try {
      localStorage.setItem('totalserp-debug-config', JSON.stringify(this.config));
    } catch (error) {
      console.warn('Failed to save debug config:', error);
    }
  }

  private loadPersistedLogs() {
    if (!this.config.persistLogs) return;
    
    try {
      const saved = localStorage.getItem(this.storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        this.entries = parsed.slice(-this.config.maxEntries);
      }
    } catch (error) {
      console.warn('Failed to load persisted logs:', error);
    }
  }

  private persistLogs() {
    if (!this.config.persistLogs) return;
    
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.entries));
    } catch (error) {
      console.warn('Failed to persist logs:', error);
    }
  }

  private notifyListeners() {
    this.listeners.forEach(listener => listener());
  }

  private shouldLog(level: DebugLevel, module: DebugModule): boolean {
    if (!this.config.enabled) return false;
    if (!this.config.modules.includes(module)) return false;
    if (LEVEL_HIERARCHY[level] < LEVEL_HIERARCHY[this.config.level]) return false;
    return true;
  }

  log(level: DebugLevel, module: DebugModule, message: string, data?: any, component?: string) {
    if (!this.shouldLog(level, module)) return;

    const entry: DebugEntry = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now(),
      level,
      module,
      component,
      message,
      data,
      stack: level === 'error' ? new Error().stack : undefined
    };

    // Add to entries
    this.entries.push(entry);

    // Trim to max entries
    if (this.entries.length > this.config.maxEntries) {
      this.entries = this.entries.slice(-this.config.maxEntries);
    }

    // Persist if enabled
    this.persistLogs();

    // Also log to console if enabled
    if (this.config.showInConsole) {
      const consoleMethod = level === 'error' ? 'error' : 
                           level === 'warn' ? 'warn' : 
                           level === 'info' ? 'info' : 'log';
      
      console[consoleMethod](`[${module}${component ? `:${component}` : ''}] ${message}`, data || '');
    }

    // Notify listeners
    this.notifyListeners();
  }

  // Convenience methods
  error(module: DebugModule, message: string, data?: any, component?: string) {
    this.log('error', module, message, data, component);
  }

  warn(module: DebugModule, message: string, data?: any, component?: string) {
    this.log('warn', module, message, data, component);
  }

  info(module: DebugModule, message: string, data?: any, component?: string) {
    this.log('info', module, message, data, component);
  }

  debug(module: DebugModule, message: string, data?: any, component?: string) {
    this.log('debug', module, message, data, component);
  }

  trace(module: DebugModule, message: string, data?: any, component?: string) {
    this.log('trace', module, message, data, component);
  }

  getEntries(): DebugEntry[] {
    return [...this.entries];
  }

  clearLogs() {
    this.entries = [];
    this.persistLogs();
    this.notifyListeners();
  }

  exportLogs(): string {
    const exportData = {
      timestamp: new Date().toISOString(),
      config: this.config,
      entries: this.entries
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.href = url;
    a.download = `totalserp-debug-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    return exportData.timestamp;
  }

  filterByModule(module: DebugModule): DebugEntry[] {
    return this.entries.filter(entry => entry.module === module);
  }

  filterByLevel(level: DebugLevel): DebugEntry[] {
    const minLevel = LEVEL_HIERARCHY[level];
    return this.entries.filter(entry => LEVEL_HIERARCHY[entry.level] >= minLevel);
  }

  searchLogs(query: string): DebugEntry[] {
    const lowerQuery = query.toLowerCase();
    return this.entries.filter(entry => 
      entry.message.toLowerCase().includes(lowerQuery) ||
      entry.component?.toLowerCase().includes(lowerQuery) ||
      JSON.stringify(entry.data).toLowerCase().includes(lowerQuery)
    );
  }

  getStats(): DebugStats {
    const stats: DebugStats = {
      totalEntries: this.entries.length,
      entriesByLevel: {
        error: 0,
        warn: 0,
        info: 0,
        debug: 0,
        trace: 0
      },
      entriesByModule: {
        'serp-tool': 0,
        'dataforseo-tool': 0,
        'ai-analyst': 0,
        'explore': 0,
        'snowflake': 0,
        'qlik': 0,
        'auth': 0,
        'api': 0,
        'general': 0
      }
    };

    this.entries.forEach(entry => {
      stats.entriesByLevel[entry.level]++;
      stats.entriesByModule[entry.module]++;
    });

    if (this.entries.length > 0) {
      stats.oldestEntry = Math.min(...this.entries.map(e => e.timestamp));
      stats.newestEntry = Math.max(...this.entries.map(e => e.timestamp));
    }

    return stats;
  }

  getModuleStats(): Record<string, number> {
    const stats: Record<string, number> = {};
    this.entries.forEach(entry => {
      stats[entry.module] = (stats[entry.module] || 0) + 1;
    });
    return stats;
  }

  updateConfig(newConfig: Partial<DebugConfig>) {
    this.config = { ...this.config, ...newConfig };
    this.saveConfig();
    this.notifyListeners();
  }

  getConfig(): DebugConfig {
    return { ...this.config };
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
}

// Singleton instance
const debugLogger = new DebugLogger();

// Import for setting global module context
let setCurrentModuleGlobal: ((module: DebugModule) => void) | null = null;

// Define explicit types for module-scoped and generic debug methods
type ModuleScopedDebugMethods = {
  log: (message: string, data?: any, component?: string) => void;
  info: (message: string, data?: any, component?: string) => void;
  warn: (message: string, data?: any, component?: string) => void;
  warning: (message: string, data?: any, component?: string) => void;
  error: (message: string, data?: any, component?: string) => void;
  trace: (message: string, data?: any, component?: string) => void;
};

type GenericDebugMethods = {
  log: (module: DebugModule, message: string, data?: any, component?: string) => void;
  info: (module: DebugModule, message: string, data?: any, component?: string) => void;
  warn: (module: DebugModule, message: string, data?: any, component?: string) => void;
  warning: (module: DebugModule, message: string, data?: any, component?: string) => void;
  error: (module: DebugModule, message: string, data?: any, component?: string) => void;
  trace: (module: DebugModule, message: string, data?: any, component?: string) => void;
};

// React hook
export const useDebugLogger = (moduleOrAutoClear?: DebugModule | boolean, autoClearOnMount: boolean = true): {
  debug: ModuleScopedDebugMethods | GenericDebugMethods;
  log: (module: DebugModule, message: string, data?: any, component?: string) => void;
  error: (module: DebugModule, message: string, data?: any, component?: string) => void;
  warn: (module: DebugModule, message: string, data?: any, component?: string) => void;
  info: (module: DebugModule, message: string, data?: any, component?: string) => void;
  debugLog: (module: DebugModule, message: string, data?: any, component?: string) => void;
  trace: (module: DebugModule, message: string, data?: any, component?: string) => void;
  entries: DebugEntry[];
  stats: DebugStats;
  config: DebugConfig;
  clearLogs: () => void;
  exportLogs: () => string;
  filterByModule: (module: DebugModule) => DebugEntry[];
  filterByLevel: (level: DebugLevel) => DebugEntry[];
  searchLogs: (query: string) => DebugEntry[];
  getModuleStats: (module: DebugModule) => any;
  updateConfig: (updates: Partial<DebugConfig>) => void;
  currentModule?: DebugModule;
  isModuleScoped: boolean;
} => {
  // Handle overloaded parameters
  const module = typeof moduleOrAutoClear === 'string' ? moduleOrAutoClear : undefined;
  const shouldAutoClear = typeof moduleOrAutoClear === 'boolean' ? moduleOrAutoClear : autoClearOnMount;
  const [, forceUpdate] = useState({});
  const unsubscribeRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    unsubscribeRef.current = debugLogger.subscribe(() => {
      forceUpdate({});
    });

    // Auto-set module filter when using module-scoped logger
    if (module && setCurrentModuleGlobal) {
      setCurrentModuleGlobal(module);
    }

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, [module]);

  // Auto-clear logs on mount (page refresh/initial load)
  useEffect(() => {
    if (shouldAutoClear) {
      debugLogger.clearLogs();
      debugLogger.info('general', 'Debug logs cleared on page mount', {
        timestamp: new Date().toISOString(),
        reason: 'auto-clear-on-mount'
      }, 'useDebugLogger');
    }
  }, []); // Empty dependency array means this runs once on mount

  return {
    // Debug object with methods - auto-scoped if module provided
    debug: module ? ({
      // Module-scoped methods (no need to pass module each time)
      log: (message: string, data?: any, component?: string) => 
        debugLogger.debug(module, message, data, component),
      info: (message: string, data?: any, component?: string) => 
        debugLogger.info(module, message, data, component),
      warn: (message: string, data?: any, component?: string) => 
        debugLogger.warn(module, message, data, component),
      warning: (message: string, data?: any, component?: string) => 
        debugLogger.warn(module, message, data, component), // Alias for warn
      error: (message: string, data?: any, component?: string) => 
        debugLogger.error(module, message, data, component),
      trace: (message: string, data?: any, component?: string) => 
        debugLogger.trace(module, message, data, component)
    } as ModuleScopedDebugMethods) : ({
      // Generic methods (need to pass module each time)
      log: (module: DebugModule, message: string, data?: any, component?: string) => 
        debugLogger.debug(module, message, data, component),
      info: (module: DebugModule, message: string, data?: any, component?: string) => 
        debugLogger.info(module, message, data, component),
      warn: (module: DebugModule, message: string, data?: any, component?: string) => 
        debugLogger.warn(module, message, data, component),
      warning: (module: DebugModule, message: string, data?: any, component?: string) => 
        debugLogger.warn(module, message, data, component), // Alias for warn
      error: (module: DebugModule, message: string, data?: any, component?: string) => 
        debugLogger.error(module, message, data, component),
      trace: (module: DebugModule, message: string, data?: any, component?: string) => 
        debugLogger.trace(module, message, data, component)
    } as GenericDebugMethods),

    // Legacy individual methods (for backward compatibility)
    log: debugLogger.log.bind(debugLogger),
    error: debugLogger.error.bind(debugLogger),
    warn: debugLogger.warn.bind(debugLogger),
    info: debugLogger.info.bind(debugLogger),
    debugLog: debugLogger.debug.bind(debugLogger),
    trace: debugLogger.trace.bind(debugLogger),

    // Data access
    entries: debugLogger.getEntries(),
    stats: debugLogger.getStats(),
    config: debugLogger.getConfig(),

    // Utilities
    clearLogs: debugLogger.clearLogs.bind(debugLogger),
    exportLogs: debugLogger.exportLogs.bind(debugLogger),
    filterByModule: debugLogger.filterByModule.bind(debugLogger),
    filterByLevel: debugLogger.filterByLevel.bind(debugLogger),
    searchLogs: debugLogger.searchLogs.bind(debugLogger),
    getModuleStats: debugLogger.getModuleStats.bind(debugLogger),
    updateConfig: debugLogger.updateConfig.bind(debugLogger),
    
    // Module information
    currentModule: module,
    isModuleScoped: !!module
  };
};

// Function to register the global setCurrentModule function
export const registerModuleSetter = (setter: (module: DebugModule) => void) => {
  setCurrentModuleGlobal = setter;
};

// Export singleton for direct access
export { debugLogger };
